import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './roadmap.css';

type RoadmapStatus = 'shipped' | 'progress' | 'planned';

interface RoadmapData {
  id: string;
  i18nKey?: string;
  title?: string;
  author: string;
  date: string;
  githubUrl: string;
  product: string;
  description?: string;
  bullets?: string[];
  shortDescription?: string;
  status?: RoadmapStatus;
  version?: string;
  /**
   * Where to anchor the modal's cover crop. That plate is far wider than it is
   * tall, so a plate image is always cropped vertically there; 'center' takes a
   * slice out of the middle, which suits the icon plates but cuts the head off
   * a screenshot. 'top' keeps the top of the frame, which is where a screenshot
   * puts the thing it is a screenshot OF. Defaults to 'center', so the plates
   * that already looked right are untouched.
   */
  imageFocus?: 'top' | 'center';
  eta?: string;
}

interface RoadmapItem extends Required<Pick<RoadmapData, 'title' | 'description' | 'bullets' | 'product'>>,
  Omit<RoadmapData, 'title' | 'description' | 'bullets' | 'product'> {
  image?: string;
}

// eslint-disable-next-line -- @typescript-eslint/no-explicit-any: require.context is a webpack extension
const roadmapImages = (require as any).context('../../assets/images/docs', false, /^\.\/roadmap-/);

function resolveImage(id: string): string | undefined {
  const target = `roadmap-${id}`;
  const key = (roadmapImages.keys() as string[]).find(
    (k) => k.replace(/^\.\//, '').replace(/\.[^.]+$/, '') === target
  );
  return key ? roadmapImages(key) : undefined;
}

/* ─── Status pill ───────────────────────────────────────────── */
const STATUS_CLASSES: Record<RoadmapStatus, string> = {
  shipped:  'text-emerald-600 dark:text-emerald-400 border-emerald-500/35 bg-emerald-500/10',
  progress: 'text-amber-600 dark:text-amber-400 border-amber-500/35 bg-amber-500/10',
  planned:  'text-slate-500 dark:text-slate-400 border-border bg-surface-muted'
};

const StatusPill: React.FC<{ status: RoadmapStatus }> = ({ status }) => {
  const { t } = useTranslation();
  const label: Record<RoadmapStatus, string> = {
    shipped:  t('status.shipped'),
    progress: t('status.inProgress'),
    planned:  t('status.planned'),
  };
  return (
    <span className={`inline-flex items-center gap-[6px] h-[22px] px-[9px] rounded-full text-[11px] font-semibold border ${STATUS_CLASSES[status]}`}>
      <span className={`w-[6px] h-[6px] rounded-full bg-current shrink-0 ${status === 'progress' ? 'animate-pulse' : ''}`} />
      {label[status]}
    </span>
  );
};

/* ─── Product tag ───────────────────────────────────────────── */
const ProductTag: React.FC<{ product: string }> = ({ product }) => (
  <span className="inline-flex items-center gap-[6px] h-[22px] px-[9px] rounded-[6px] text-[11px] font-semibold border border-blue/30 bg-blue/10 text-blue dark:text-blue">
    <span className="w-[8px] h-[8px] rounded-full bg-blue" />
    {product}
  </span>
);

/* ─── Shared media plate ────────────────────────────────────── */
interface RoadmapPlateProps {
  image?: string;
  alt: string;
  fallback: string;
  className?: string;
  imgClassName?: string;
  children?: React.ReactNode;
}

const RoadmapPlate: React.FC<RoadmapPlateProps> = ({
  image,
  alt,
  fallback,
  className = '',
  imgClassName = 'max-w-full max-h-full w-auto h-auto object-contain',
  children
}) => (
  <div className={`roadmap-plate flex items-center justify-center ${className}`}>
    {image ? (
      <img src={image} alt={alt} className={imgClassName} />
    ) : (
      <span className="text-content-muted text-[13px] font-body">{fallback}</span>
    )}
    {children}
  </div>
);

/* ─── Modal ─────────────────────────────────────────────────── */
interface ModalProps {
  item: RoadmapItem;
  onClose: () => void;
  imageComingSoon: string;
  postedOn: string;
  viewOnGithub: string;
}

const RoadmapModal: React.FC<ModalProps> = ({
  item,
  onClose,
  imageComingSoon,
  postedOn,
  viewOnGithub
}) => (
  <div
    className="fixed inset-0 bg-border-dark/50 z-[1000] flex items-center justify-center p-[24px]"
    onClick={onClose}
  >
    <div
      className="bg-surface-secondary rounded-[16px] w-full max-w-[560px] max-h-[90vh] overflow-y-auto shadow-[0_20px_60px_rgba(0,0,0,0.2)]"
      onClick={(e) => e.stopPropagation()}
    >
      <RoadmapPlate
        image={item.image}
        alt={item.title}
        fallback={imageComingSoon}
        className="rounded-t-[16px] h-[260px] relative"
        imgClassName={`w-full h-full object-cover ${
          item.imageFocus === 'top' ? 'object-top' : 'object-center'
        }`}
      >
        <button
          onClick={onClose}
          className="absolute top-[12px] right-[12px] z-10 bg-border-dark/40 border-none rounded-[6px] w-[36px] h-[26px] cursor-pointer text-white text-[14px] flex items-center justify-center"
        >
          ✕
        </button>
      </RoadmapPlate>

      <div className="font-body pt-[28px] px-[32px] pb-[32px]">
        <h2 className="font-display text-[22px] font-extrabold text-content-primary mb-[14px] tracking-[-0.3px]">
          {item.title}
        </h2>

        <p className="text-[14px] text-content-secondary mb-[4px]">
          <strong>{item.author}</strong>
          <span className="text-slate-400 mx-[6px]">|</span>
          {postedOn} {item.date}
        </p>
        <a
          href={item.githubUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[13px] text-slate-400 no-underline inline-block mb-[20px] hover:text-blue-link transition-colors duration-150"
        >
          {viewOnGithub}
        </a>

        <p className="text-[14px] leading-[1.75] text-content-secondary mb-[16px]">
          {item.description}
        </p>

        <ul className="m-0 pl-[20px] list-disc flex flex-col gap-[8px]">
          {item.bullets.map((bullet, i) => (
            <li key={i} className="text-[14px] leading-[1.65] text-content-secondary">
              {bullet}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

/* ─── Page ──────────────────────────────────────────────────── */
const TouchposeRoadmap: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState<RoadmapItem | null>(null);
  const [rawItems, setRawItems] = useState<RoadmapData[] | null>(null);

  React.useEffect(() => {
    fetch('/roadmap.json')
      .then((r) => r.json())
      .then((data: RoadmapData[]) => setRawItems(data))
      .catch(() => setRawItems([]));
  }, []);

  const items = useMemo<RoadmapItem[] | null>(() => {
    if (!rawItems) return null;
    return rawItems.map((item) => {
      const prefix = item.i18nKey ? `roadmap.${item.i18nKey}` : null;
      const bullets = prefix
        ? Array.from({ length: 20 }, (_, idx) => {
            const key = `${prefix}Bullet${idx + 1}`;
            return i18n.exists(key) ? t(key) : null;
          }).filter((b): b is string => b !== null)
        : (item.bullets ?? []);
      return {
        ...item,
        image:            resolveImage(item.id),
        title:            prefix ? t(`${prefix}Title`)  : (item.title ?? ''),
        description:      prefix ? t(`${prefix}Desc`)   : (item.description ?? ''),
        shortDescription: prefix ? t(`${prefix}Short`)  : item.shortDescription,
        bullets,
        product:          item.product,
      };
    });
  }, [rawItems, t, i18n]);

  if (!items) return null;

  return (
    <div
      className="font-body"
      style={{ padding: 'clamp(24px, 5vw, 40px) clamp(20px, 5.5vw, 72px)' }}
    >
      <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-[24px]">
        {items.map((item) => (
          <div
            key={item.id}
            onClick={() => setSelected(item)}
            className="flex flex-col h-[280px] border border-border rounded-[12px] overflow-hidden cursor-pointer bg-surface-secondary shadow-[0_1px_4px_rgba(0,0,0,0.05)] transition-[box-shadow,transform] duration-150 hover:shadow-[0_4px_16px_rgba(0,0,0,0.1)] hover:-translate-y-[2px]"
          >
            {/* Card header */}
            <div className="pt-[14px] px-[16px] pb-[12px] border-b border-border flex flex-col gap-[6px]">
              <div className="flex items-center gap-[10px]">
                <h3 className="font-display text-[15px] font-bold text-content-primary m-0 tracking-[-0.01em]">
                  {item.title}
                </h3>
                <span className="ml-auto">
                  <StatusPill status={item.status ?? 'planned'} />
                </span>
              </div>
              {item.shortDescription && (
                <p className="text-[12.5px] leading-[1.4] text-content-muted m-0">
                  {item.shortDescription}
                </p>
              )}
            </div>

            {/* Media plate */}
            <RoadmapPlate
              image={item.image}
              alt={item.title}
              fallback={t('roadmap.imageComingSoon')}
              className="flex-1 min-h-[120px]"
            />

            {/* Card footer */}
            <div className="flex items-center gap-[10px] px-[14px] py-[8px] border-t border-border text-[11px] text-content-muted font-mono tracking-[0.02em]">
              {item.product && <ProductTag product={item.product} />}
              <span>{item.version ?? 'v?.?.?'}</span>
              {item.status !== 'shipped' && (
                <>
                  <span className="w-[3px] h-[3px] rounded-full bg-current opacity-50" />
                  <span>{item.eta ?? 'TBD'}</span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <RoadmapModal
          item={selected}
          onClose={() => setSelected(null)}
          imageComingSoon={t('roadmap.imageComingSoon')}
          postedOn={t('roadmap.postedOn')}
          viewOnGithub={t('roadmap.viewOnGithub')}
        />
      )}
    </div>
  );
};

export default TouchposeRoadmap;
